<template>
    <AppModal @close="onClose()" v-bind="$attrs" v-if="visible">
        <template v-slot:header>Add Oracle Application Role</template>
        <form @submit.prevent="onSubmit()">
            <transition name="fade">
                <AppNotification :type="alertMessage.type" v-if="alertMessage.message">
                    {{alertMessage.message}}
                </AppNotification>
            </transition>
            <div class="form-group">
                <label for="moduleId">Module</label>
                <select :class="{'is-invalid': $v.moduleId.$invalid}" @change="onModuleIdChange"
                        class="form-control" v-model="moduleId">
                    <option :value="null" disabled>--Select Module--</option>
                    <option :key="module.id" :value="module.id" v-for="module in modules">{{module.name}}</option>
                </select>
                <span class="invalid-feedback" v-if="!$v.moduleId.required">Field is required</span>
            </div>
            <div class="form-group">
                <label for="moduleRoleId">Function Role</label>
                <select :class="{'is-invalid': $v.moduleRoleId.$invalid}" class="form-control"
                        v-model="moduleRoleId">
                    <option :value="null" disabled>--Select Function Role--</option>
                    <option :key="moduleRole.id" :value="moduleRole.id" v-for="moduleRole in moduleRoles">
                        {{moduleRole.name}}
                    </option>
                </select>
                <span class="invalid-feedback" v-if="!$v.moduleRoleId.required">Field is required</span>
            </div>
            <div class="form-group">
                <label for="moduleRoleId">Organization Role</label>
                <select :class="{'is-invalid': $v.organizationModuleRoleName.$invalid}" class="form-control"
                        v-model="organizationModuleRoleName">
                    <option :value="null" disabled>--Select Role--</option>
                    <option :key="organizationModuleRole" :value="organizationModuleRole"
                            v-for="organizationModuleRole in organizationModuleRoles">{{organizationModuleRole}}
                    </option>
                </select>
                <span class="invalid-feedback" v-if="!$v.organizationModuleRoleName.required">Field is required</span>
            </div>
            <div class="form-group">
                <AppCheckBox v-model="grantMyStuffAccess">Grant MyStuff Access
                </AppCheckBox>
            </div>
        </form>
        <template v-slot:footer>
            <button :disabled="isSubmitting" @click.prevent="onSubmit()" class="btn btn-primary" type="submit">Save
            </button>
            <button :disabled="isSubmitting" @click="onClose()" class="btn btn-danger" data-dismiss="modal"
                    type="button">
                Close
            </button>
        </template>
    </AppModal>
</template>

<script>
    import AppModal from "../shared/AppModal";
    import AppCheckBox from "../shared/AppCheckBox";
    import AppNotification from "@/components/shared/AppNotification";
    import {required} from "vuelidate/lib/validators";
    import OracleService from "@/services/OracleService";
    import {ToastType} from "@/services/models/shared/ToastType";

    export default {
        components: {
            AppModal,
            AppCheckBox,
            AppNotification
        },
        data() {
            return {
                moduleRoleId: null,
                moduleId: null,
                organizationModuleRoleName: null,
                grantMyStuffAccess: false,
                isSubmitting: false,
                modules: [],
                moduleRoles: [],
                organizationModuleRoles: [],
                alertMessage: {
                    type: null,
                    message: null
                },
            }
        },
        props: {
            accountId: {
                type: Number,
                required: true,
            },
            visible: {
                required: false,
                default: false
            }
        },
        watch: {
            visible() {
                this.moduleRoleId = null;
                this.moduleId = null;
                this.grantMyStuffAccess = false;
            }
        },
        methods: {
            /**
             * @description Emits the close event to the parent
             */
            onClose() {
                this.$emit('close')
            },

            /**
             *  @description When the module Id changes, we need to get the list of module roles associated with that module
             */
            onModuleIdChange(event) {
                const vm = this;

                // Reset the existing fields associated with the module id
                vm.moduleRoleId = null;
                vm.organizationModuleRoleName = null;
                vm.grantMyStuffAccess = false;

                OracleService.getModuleRoles(event.target.value)
                    .then(response => {
                        vm.moduleRoles = response;
                    }).catch(function (error) {
                    vm.alertMessage.type = "danger";
                    vm.alertMessage.message = error.response.statusText;
                })
            },

            /**
             * @description Creates a new account module role and emits the event to the parent
             */
            onSubmit() {
                const vm = this;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                OracleService.addAccountModuleRole(vm.accountId, {
                    grantMyStuffAccess: vm.grantMyStuffAccess,
                    moduleRoleId: vm.moduleRoleId,
                    organizationRoleName: vm.organizationModuleRoleName
                }).then(() => {
                    vm.alert(ToastType.Success, "The role was successfully created.");

                    vm.$emit('create');
                    vm.onClose();
                }).catch(function (error) {
                    vm.alertMessage.type = "danger";
                    vm.alertMessage.message = error.response.statusText;
                }).finally(() => {
                    vm.isSubmitting = false;
                });
            }
        },
        created() {
            const vm = this;

            // Load the modules
            Promise.all([OracleService.getAllModules()
                .then(response => {
                    vm.modules = response;
                }),

                OracleService.getAllModuleOrganizationRoles()
                    .then(response => {
                        // Get the distinct role list and sort it
                        vm.organizationModuleRoles = [...new Set(response.map(function (e) {
                            return e.role;
                        }))].sort();
                    })
            ])
                .catch((error) => {
                    vm.alert(ToastType.Danger, error.response.statusText);

                    vm.onClose();
                })
        },
        validations: {
            moduleRoleId: {
                required
            },
            moduleId: {
                required
            },
            organizationModuleRoleName: {
                required
            }
        }
    }
</script>
